"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Loggy = exports.LoggyLevel = void 0;
var LoggyLevel;
(function (LoggyLevel) {
    /** Alias for log to be backwards-compatible */
    LoggyLevel["debug"] = "debug";
    /** Everything */
    LoggyLevel["log"] = "log";
    /** Important things */
    LoggyLevel["info"] = "info";
    /** Warnings that something goes wrong */
    LoggyLevel["warn"] = "warn";
    /** Error */
    LoggyLevel["error"] = "error";
    /** Do not log at all */
    LoggyLevel["silent"] = "silent";
    LoggyLevel["off"] = "off";
})(LoggyLevel = exports.LoggyLevel || (exports.LoggyLevel = {}));
class Loggy {
    constructor(loggyConfig) {
        this.logLevelOrder = {
            silent: 0,
            error: 1,
            warn: 2,
            info: 3,
            debug: 4,
            log: 4,
        };
        const { loggers, bindConsole } = loggyConfig;
        this.loggerConfigs = loggers;
        // directly bind to a given console reference
        if (bindConsole) {
            const { consoleRef, level } = bindConsole;
            if (this.logLevelAllowed(level, LoggyLevel.error)) {
                this.error = consoleRef.error.bind(consoleRef);
            }
            if (this.logLevelAllowed(level, LoggyLevel.warn)) {
                this.warn = consoleRef.warn.bind(consoleRef);
            }
            if (this.logLevelAllowed(level, LoggyLevel.info)) {
                this.info = consoleRef.info.bind(consoleRef);
            }
            if (this.logLevelAllowed(level, LoggyLevel.log)) {
                this.log = consoleRef.log.bind(consoleRef);
            }
            if (this.logLevelAllowed(level, LoggyLevel.debug)) {
                this.debug = consoleRef.debug.bind(consoleRef);
            }
        }
    }
    group() { }
    groupEnd() { }
    debug(contextRefOrSignature, ...args) {
        this.process([LoggyLevel.log, LoggyLevel.debug], contextRefOrSignature, ...args);
    }
    log(contextRefOrSignature, ...args) {
        this.process([LoggyLevel.log, LoggyLevel.debug], contextRefOrSignature, ...args);
    }
    info(contextRefOrSignature, ...args) {
        this.process([LoggyLevel.info], contextRefOrSignature, ...args);
    }
    warn(contextRefOrSignature, ...args) {
        this.process([LoggyLevel.warn], contextRefOrSignature, ...args);
    }
    error(contextRefOrSignature, ...args) {
        this.process([LoggyLevel.error], contextRefOrSignature, ...args);
    }
    format(contextRefOrSignature, path, message) {
        return this.getSignature(contextRefOrSignature, path) + message;
    }
    process(methods, contextRefOrSignature, ...args) {
        if (!args) {
            args = [];
        }
        for (const config of this.loggerConfigs) {
            for (const method of methods) {
                if (this.logLevelAllowed(method, config.logLevel) && this.methodSupported(method, config)) {
                    const signature = this.getSignature(contextRefOrSignature, args[0]);
                    if (this.applyRegexFilters(method, signature, config)) {
                        if (this.isSignature(contextRefOrSignature)) {
                            // pathOrArg is an argument
                            config.logger[method].apply(config.logger, [signature, ...args]);
                        }
                        else {
                            // pathOrArg is a path
                            config.logger[method](...args.slice(1), signature);
                        }
                        break; // the method loop
                    }
                }
            }
        }
    }
    /**
     * Allow if method`s log level is equal or lower.
     */
    logLevelAllowed(method, level) {
        return (method !== LoggyLevel.off && level !== LoggyLevel.off && this.logLevelOrder[method] <= this.logLevelOrder[level]);
    }
    /**
     * Allow only if regex matches
     */
    applyRegexFilters(method, signature, config) {
        // allow all logs if logOnly is not set
        if (!config.regexFilters)
            return true;
        // allow all logs if logOnly is not set for this particular method
        if (!config.regexFilters[method])
            return true;
        // allow only logs which fit to at least one given pattern
        for (const pattern of config.regexFilters[method]) {
            if (new RegExp(pattern).test(signature))
                return true;
        }
        return false;
    }
    methodSupported(method, config) {
        return method in config.logger;
    }
    /**
     * Retrieves the signature of a log message. The signature consists of a context name and a path.
     *
     * The context name can be one of each:
     * - class name
     * - (() => void) name
     * - anonymous
     *
     * The path name can be used in a flexible way to describe the use-case, i.e.:
     * - method name
     * - event name
     * - subscription name
     * ... or unknown combination of them like:
     * - method name $ subscription name (for a subscription callback inside a method)
     *
     * To provide flexibility and support legacy signature creation like
     * `logger.log('ClassName.methodName', arg1, ..., argN);`
     * we allow the first param to be a string and the second to be any kind of argument.
     *
     * @param contextRefOrSignature Either the ref to the context ((() => void), class proto, class
     * instance) or a string (for flexibility and legacy support)
     * @param pathOrArg Either the path to the log message`s usage
     *                  of an argument (for flexibility and legacy support)
     * @returns The signature
     */
    getSignature(contextRefOrSignature, pathOrArg) {
        let contextName;
        // immediately return a signature
        if (this.isSignature(contextRefOrSignature)) {
            return contextRefOrSignature;
        }
        // create a context name for a class instance
        else if (typeof contextRefOrSignature === 'object' && contextRefOrSignature != null) {
            contextName = Object.getPrototypeOf(contextRefOrSignature).constructor.name;
        }
        // create a context name for a class proto
        else if (typeof contextRefOrSignature === 'function') {
            contextName = contextRefOrSignature.name;
        }
        // always use anonymous as fallback when we could not determine a context name
        if (!contextName || contextName === 'Object') {
            contextName = 'ANONYMOUS';
        }
        return contextName + '.' + pathOrArg;
    }
    isSignature(contextRefOrSignature) {
        return typeof contextRefOrSignature === 'string';
    }
}
exports.Loggy = Loggy;
//# sourceMappingURL=Loggy.js.map